<template>
    <div
        class="hover-card"
        :class="{ 'for-multiselect': forMultiselect }"
        :style="{ height }">
        <slot name="header">
            <div class="hover-card__header">
                <slot name="actions" />
                <div class="hover-card__title_and_subtitle_wrapper">
                    <h2
                        v-pendo-tooltip="{ content: showTitleTooltip ? title : null, arrow: true, multiline: true }"
                        class="hover-card__title"
                        :style="titleTruncationStyle"
                        @mouseover="checkTitleOverflow($event)">
                        {{ title }}
                    </h2>
                    <slot name="subtitle" />
                </div>
            </div>
        </slot>
        <pendo-divider height="1px" />
        <div class="hover-card__body">
            <slot name="body" />
        </div>
        <div class="hover-card__metrics">
            <div v-if="metricsTitle || metricsSubtitle">
                <span
                    v-if="metricsTitle"
                    class="hover-card__metrics-title">
                    {{ metricsTitle }}
                </span>
                {{ metricsSubtitle }}
            </div>
            <div class="hover-card__metrics-flex-container">
                <div
                    v-for="metric in metrics"
                    :key="metric.label"
                    v-pendo-tooltip="{ content: metric.tooltip, arrow: true }"
                    class="hover-card__metrics-flex-item">
                    <pendo-icon
                        v-if="hasMetricsError || metric.error"
                        type="minus-circle"
                        size="12" />
                    <div
                        v-else
                        class="hover-card__metrics-number">
                        <pendo-icon
                            :type="metric.icon"
                            size="12" />
                        <pendo-loading-indicator
                            v-if="isLoadingMetrics || metric.loading"
                            type="skeleton"
                            :skeleton-props="{ width: 35, height: 14 }" />
                        <span v-else>
                            {{ formatMetricValue(metric.value) }}
                        </span>
                    </div>
                    {{ metric.label }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { PendoDivider, PendoIcon, PendoLoadingIndicator, PendoTooltip } from '@pendo/components';
import { formatNumberWithSuffix } from '@/utils/formatters';

export default {
    name: 'HoverCard',
    components: {
        PendoDivider,
        PendoIcon,
        PendoLoadingIndicator,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        titleLinesToShow: {
            type: Number,
            default: 2,
        },
        height: {
            type: String,
            default: '320px',
        },
        /**
         * Format: Array of Objects [{ icon: String, label: String, value: number, tooltip: String, loading: boolean, error: boolean }]
         */
        metrics: {
            type: Array,
            required: true,
        },
        isLoadingMetrics: {
            type: Boolean,
            default: false,
        },
        hasMetricsError: {
            type: Boolean,
            default: false,
        },
        metricsTitle: {
            type: String,
            default: null,
        },
        metricsSubtitle: {
            type: String,
            default: '',
        },
        forMultiselect: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            showTitleTooltip: false,
        };
    },
    computed: {
        titleTruncationStyle () {
            return {
                '--lines-to-show': this.titleLinesToShow,
            };
        },
    },
    methods: {
        formatMetricValue (metricValue) {
            return formatNumberWithSuffix(metricValue);
        },
        checkTitleOverflow ($event) {
            const { scrollHeight } = $event.target;
            const maxTitleHeight = this.titleLinesToShow * 20 + 3; // each line is 20px tall + 3px padding
            this.showTitleTooltip = scrollHeight > maxTitleHeight;
        },
    },
};
</script>

<style scoped lang="less">
.hover-card {
    border-radius: 3px;
    border: 0.5px solid var(--palette-gray-40);
    background: var(--palette-gray-0);
    display: flex;
    width: 320px;
    padding: 16px;
    flex-direction: column;
    gap: 16px;
    font-weight: 400;
    font-family: Inter;
    box-shadow: 0px 4px 4px 0px var(--semantic-drop-shadow-base);

    &.for-multiselect {
        border: none;
        box-shadow: none;
    }

    &__header {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    &__title {
        font-size: 14.222px;
        line-height: 21px;
        font-weight: 600;
        width: 100%;
        min-height: 21px;
        max-height: 42px;
        display: -webkit-box;
        -webkit-line-clamp: var(--lines-to-show);
        line-clamp: var(--lines-to-show);
        -webkit-box-orient: vertical;
        overflow: hidden;
        word-wrap: break-word;
        margin-top: 0px;
        margin-bottom: 0px;
    }

    &__title_and_subtitle_wrapper {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    :deep(.pendo-divider) {
        margin: 0;
    }

    &__body {
        flex-grow: 2;
    }

    &__metrics {
        color: var(--palette-gray-70);
        font-size: 10.28px;
        &-title {
            font-size: 12.642px;
            font-weight: 600;
            margin-right: 4px;
        }
        &-flex-container {
            background: var(--palette-gray-10);
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            margin-top: 8px;
            border-radius: 3.395px;
        }
        &-flex-item {
            font-size: 10px;
            display: flex;
            height: 54px;
            padding: 4.653px;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 2px;
            flex: 1 0 0;
            border-right: 0.5px solid var(--palette-gray-40);

            :deep(.pendo-icon) {
                color: var(--palette-gray-110);
                padding-bottom: 1px;
            }
            :deep(.pendo-icon__minus-circle) {
                color: var(--palette-red-70);
            }
        }
        &-flex-item:last-child {
            border-right: none;
        }
        &-number {
            display: flex;
            align-items: center;
            gap: 4.65px;
            color: var(--palette-gray-110);
            font-size: 12.642px;
            font-weight: 600;
        }
    }
}
</style>
